// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import type { ReviewStatus, Sample, SurveyType } from '../../../api/types';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { approveLock, approveLockTitle } from '../../../bygning/review';
import { Button } from '../../Button';
import { Kbd } from '../../Kbd';
import styles from './Inspector.module.css';

export interface ReviewActionsProps {
  type: SurveyType;
  samples: readonly Sample[];
  busy: boolean;
  /** Runs `actions.review`; a server 422 toasts the open samples there. */
  onReview: (status: ReviewStatus) => void;
  /** A control first in the same row (Ressourcer's Markér vigtig star). */
  lead?: ReactNode;
}

/**
 * The inspector's footer (v8 `.act`): pinned to the foot of the tile, sticky
 * while the type's fields scroll under it, with `lead` (if any), Fejl (A) and
 * Godkend (G) in one row. Godkend is disabled while samples are open, with the
 * reason under it (the server refuses with 422 anyway). A reviewed type offers
 * "Gendan til kø" / "Tilbage i kø" instead of repeating its own status.
 * Renders nothing — no empty bar — for a viewer (`useCanEdit`).
 */
export function ReviewActions({ type, samples, busy, onReview, lead }: ReviewActionsProps) {
  const canEdit = useCanEdit();
  if (!canEdit) return null;
  const lock = approveLock(type, samples);
  const status = type.review_status;
  return (
    <div className={styles.footer}>
      <div className={styles.actions}>
        {lead}
        {status === 'rejected' ? (
          <Button size="lg" className={styles.grow} disabled={busy} onClick={() => onReview('queue')}>
            Gendan til kø
          </Button>
        ) : (
          <Button size="lg" title="Markér typen som fejldetektion" disabled={busy} onClick={() => onReview('rejected')}>
            Fejl <Kbd>A</Kbd>
          </Button>
        )}
        {status === 'approved' ? (
          <Button size="lg" className={styles.grow} disabled={busy} onClick={() => onReview('queue')}>
            Tilbage i kø
          </Button>
        ) : (
          status === 'queue' && (
            <Button
              variant="primary"
              size="lg"
              className={styles.grow}
              disabled={busy || lock !== null}
              title={approveLockTitle(type, samples) ?? undefined}
              onClick={() => onReview('approved')}
            >
              Godkend <Kbd>G</Kbd>
            </Button>
          )
        )}
      </div>
      {status === 'queue' && lock && <p className={styles.lock}>{lock}</p>}
    </div>
  );
}
